/**
 * Incident types for one-tap reporting ("Waze for compliance"). Each type carries a default severity,
 * who gets notified, and the first steps shown to the reporter — all from docs/source.
 */
import type { IncidentType, Role, Severity } from "./model.ts";
import { MENU_ITEMS } from "./playbooks.ts";
import { EASY_PROCEDURES } from "./easy.ts";
import { INJURY_QUESTIONS, injuryLines, parseInjury, type InjuryAnswers } from "./injury.ts";

/**
 * One list per event (screen analysis 05, fix R3): where an event's "Do this first" and its step-by-step used to be two
 * lists that disagreed (the vomit bleach time, hot-hold), the report's first steps are the Easy steps' words.
 * easy.ts imports nothing, so this is safe at load.
 */
const easySays = (incident: string): string[] => EASY_PROCEDURES.find((p) => p.for.incident === incident)!.steps.map((s) => s.say);

export interface IncidentTypeInfo {
  type: IncidentType;
  label: string;
  severity: Severity;
  notify: Role[];
  firstSteps: string[];
  externalReport?: "county_health" | "pest_company";
  requirementIds: string[];
  /** Two open reports of the same type in the same area within this many hours are merged (confirm instead of duplicate). */
  dedupeHours: number;
  /**
   * What the report asks after the first steps. "place": "Where is it?" (a room from the location, or "Somewhere else:
   * …"). "person": something happened to a person, so it asks `questions` and where is optional. "private": a sick
   * report, which never records where (docs/PEOPLE_AND_NEGATION.md T4).
   */
  ask: IncidentAsk;
  /** Person reports: the questions asked, in order (one per screen on the phone). Never who, never medical details. */
  questions?: IncidentQuestion[];
  /** Show the first of `firstSteps` big, above everything (a life-safety step). */
  bigFirstStep?: boolean;
  /** A mess report (mess.ts): "What is it?" and "What is it on?", then steps composed for that mix. */
  mess?: boolean;
}

export type IncidentAsk = "place" | "person" | "private";
/** dish: "Which dish?" / "What did they eat?"; allergens: "Which allergen?"; ateWhen: "When did they eat?". */
export type IncidentQuestion = "dish" | "allergens" | "ateWhen" | "hospital" | "food" | "covered";

export const INCIDENT_TYPES: IncidentTypeInfo[] = [
  { type: "pest", ask: "place", label: "Pest sighting", severity: "high", notify: ["manager", "admin"], dedupeHours: 72, externalReport: "pest_company", requirementIds: ["fc-pest-control"],
    firstSteps: ["Stop using food and supplies it may have touched.", "Cover or throw away that food.", "Don't touch bats, birds or droppings.", "Never spray or set out poison yourself."] },
  { type: "temperature", ask: "place", label: "Fridge, freezer or hot-hold out of range", severity: "high", notify: ["manager"], dedupeHours: 12, requirementIds: ["fc-holding"],
    firstSteps: easySays("temperature") },
  { type: "illness", ask: "private", label: "I'm sick or was exposed", severity: "high", notify: ["manager"], dedupeHours: 0, requirementIds: ["fc-employee-health"],
    firstSteps: ["Stop food work now.", "Let the person in charge decide your work today.", "Don't type medical details. Yes or no is enough."] },
  { type: "customer_illness_complaint", ask: "person", questions: ["dish", "ateWhen"], label: "Customer says they got sick", severity: "urgent", notify: ["manager", "admin", "owner"], dedupeHours: 24, externalReport: "county_health", requirementIds: ["fc-employee-health"],
    firstSteps: ["Write their name and how to reach them.", "Write how they feel, when it started, and what they ate.", "Keep leftover food from their order in the fridge.", "Label it \"Do not serve.\"", "Call the county health department today."] },
  { type: "vomit_diarrhea_cleanup", ask: "place", label: "Vomit or diarrhea in the building", severity: "urgent", notify: ["manager"], dedupeHours: 2, requirementIds: ["fc-vomit-cleanup"],
    firstSteps: easySays("vomit_diarrhea_cleanup") },
  { type: "injury_cut_burn", ask: "person", questions: ["hospital", "food", "covered"], label: "Cut or burn", severity: "normal", notify: ["manager"], dedupeHours: 0, requirementIds: ["fc-bare-hand"],
    firstSteps: easySays("injury_cut_burn") },
  // A fire (firesafety.ts): people first, so the first step is big (get out) and 911 is next. Where is optional: a report never walls a fire.
  { type: "fire", ask: "person", questions: [], bigFirstStep: true, label: "Fire or smoke", severity: "urgent", notify: ["manager", "admin", "owner"], dedupeHours: 0, requirementIds: ["ut-fire-equipment", "ut-mfb-fire"],
    firstSteps: easySays("fire") },
  { type: "equipment_failure", ask: "place", label: "Equipment broken", severity: "normal", notify: ["manager"], dedupeHours: 48, requirementIds: [],
    firstSteps: ["Stop using it and tag it.", "Use the three-compartment sink if the dishwasher isn't hot or sanitizing."] },
  { type: "water_power_outage", ask: "place", label: "Water or power out", severity: "urgent", notify: ["manager", "admin"], dedupeHours: 6, requirementIds: ["fc-holding"],
    firstSteps: ["Stop serving food if the water is off.", "Stop if there is a boil-water notice.", "Stop food work where hands cannot be washed.", "Keep fridge doors shut.", "Write down the temperatures.", "Throw away cold food above 41°F for more than 4 hours.", "Check the hand sink water once the water is back."] },
  { type: "chemical_spill", ask: "place", label: "Chemical spill or exposure", severity: "high", notify: ["manager"], dedupeHours: 6, requirementIds: ["fc-chemicals"],
    firstSteps: ["Move people and food away.", "Open doors and windows.", "Rinse eyes or skin with water for 15 minutes.", "Read part 6 of the chemical's safety sheet for spills.", "Call Poison Control: 1-800-222-1222."] },
  { type: "allergen_reaction", ask: "person", questions: ["dish", "allergens"], bigFirstStep: true, label: "Allergic reaction", severity: "urgent", notify: ["manager", "admin"], dedupeHours: 0, requirementIds: ["fc-allergens"],
    firstSteps: ["Call 911 for trouble breathing or swelling.", "Help them use their EpiPen.", "Keep the dish and its ingredient list."] },
  { type: "contamination", ask: "place", label: "Food contaminated or bare-hand contact", severity: "normal", notify: ["manager"], dedupeHours: 4, requirementIds: ["fc-bare-hand"],
    firstSteps: ["Throw away the food.", "Wash your hands and change gloves.", "Clean and sanitize the surface."] },
  // "Something's dirty or spilled" (mess.ts): asks what it is and what it's on, then composes the steps for that mix.
  // Severity, who hears and the title come from mess.ts messReport (a small mess fixed now notifies nobody).
  { type: "mess", ask: "place", mess: true, label: "Something's dirty or spilled", severity: "low", notify: [], dedupeHours: 2, requirementIds: [],
    firstSteps: ["Keep people away from it.", "Put a wet floor sign by a wet floor."] },
  { type: "other", ask: "place", label: "Something else", severity: "low", notify: ["manager"], dedupeHours: 24, requirementIds: [], firstSteps: ["Tell us what happened and add a photo."] },
];

export function incidentInfo(t: IncidentType): IncidentTypeInfo {
  return INCIDENT_TYPES.find((x) => x.type === t) ?? INCIDENT_TYPES[INCIDENT_TYPES.length - 1]!;
}

/** A sick report ("I'm sick or was exposed"): private to the reporter (T4). */
export const isPrivateType = (t: IncidentType): boolean => incidentInfo(t).ask === "private";

/**
 * A private (sick) report as anyone may keep or see it: no typed words, no "already did" and no photos, only that it
 * happened (FRAMEWORK T4, STANDARDS S8: managers see actions, never symptoms). The API strips these on the way in and
 * redacts them on the way out, so rows stored before the web's free-text box was removed never reach a manager.
 * Other reports come back unchanged (the same object).
 */
export function withoutPrivateText<T extends { type: IncidentType; details?: string; immediateActions?: string; photoPaths?: string[] }>(i: T): T {
  if (!isPrivateType(i.type)) return i;
  if (i.details === undefined && i.immediateActions === undefined && !i.photoPaths?.length) return i;
  const { details: _d, immediateActions: _a, ...rest } = i;
  return { ...rest, ...(i.photoPaths ? { photoPaths: [] } : {}) } as T;
}

/**
 * Waze-style dedupe: an open incident of the same type and area reported recently is confirmed, not duplicated.
 * Person reports never merge: two guests who got sick are two reports, even at the same table.
 */
export function findDuplicate<T extends { type: IncidentType; area: string; status: string; reportedAt: string }>(open: T[], type: IncidentType, area: string, now = Date.now()): T | undefined {
  const info = incidentInfo(type);
  const hours = info.dedupeHours;
  if (!hours || info.ask === "person") return undefined;
  return open.find((i) => i.type === type && i.area === area && i.status !== "resolved" && now - Date.parse(i.reportedAt) <= hours * 3600_000);
}

/* ─────────────── the report's own questions ─────────────── */

/** The client's free-text place: "Somewhere else: patio". Accepted for place reports even when not in the room list. */
export const SOMEWHERE_ELSE = "Somewhere else";
export const somewhereElse = (text: string) => `${SOMEWHERE_ELSE}: ${text.trim()}`;
/** "Somewhere else: patio" → "patio"; a named room stays as it is; "" stays "". */
export const areaLabel = (area: string) => area.replace(/^Somewhere else:\s*/i, "");
/** True for "Somewhere else: <some text>". */
export const isSomewhereElse = (area: string) => /^Somewhere else:\s*\S/i.test(area);

/**
 * The 9 major allergens, as library.ts "The 9 major allergens" names them (FALCPA, FASTER Act). Ids match
 * playbooks.ts MenuItem.allergens, so a dish's allergens can be compared with what was reported.
 */
export const MAJOR_ALLERGENS: { id: string; label: string }[] = [
  { id: "milk", label: "Milk" }, { id: "eggs", label: "Eggs" }, { id: "peanuts", label: "Peanuts" }, { id: "tree nuts", label: "Tree nuts" },
  { id: "soy", label: "Soy" }, { id: "wheat", label: "Wheat" }, { id: "fish", label: "Fish" }, { id: "shellfish", label: "Shellfish (shrimp, crab, lobster)" },
  { id: "sesame", label: "Sesame" },
];
export const ALLERGEN_NOT_SURE = "not sure";
export const ALLERGEN_IDS: readonly string[] = [...MAJOR_ALLERGENS.map((a) => a.id), ALLERGEN_NOT_SURE];
export const allergenLabel = (id: string) => (id === ALLERGEN_NOT_SURE ? "Not sure" : MAJOR_ALLERGENS.find((a) => a.id === id)?.label ?? id);

/** "When did they eat?" choices. Plain words; a time of day can go in the details. */
export const ATE_WHEN_CHOICES = ["Earlier today", "Yesterday", "2 or more days ago", "Not sure"] as const;

/** Allergen picks: "Not sure" stands alone; picking an allergen clears it. */
export function toggleAllergen(picked: readonly string[], id: string): string[] {
  if (id === ALLERGEN_NOT_SURE) return picked.includes(id) ? [] : [id];
  const rest = picked.filter((x) => x !== ALLERGEN_NOT_SURE);
  return rest.includes(id) ? rest.filter((x) => x !== id) : [...rest, id];
}

/** The question each report asks about its place. Person reports: optional ("Table or area (optional)"). */
export function placeQuestion(t: IncidentType): string {
  if (incidentInfo(t).ask !== "person") return "Where is it?";
  return t === "injury_cut_burn" ? "Where did it happen? (optional)" : t === "fire" ? "Where did it start? (optional)" : "Table or area (optional)";
}

/**
 * The title a report gets: "Pest sighting in Storage", "Pest sighting in patio" (from "Somewhere else: patio"),
 * "Allergic reaction: Lattes in Table 4", "Cut or burn". The demos join a place report with ": " (`sep`), as they always have.
 */
export function incidentTitle(t: IncidentType, area: string, dish?: string, sep: " in " | ": " = " in "): string {
  const info = incidentInfo(t);
  const where = areaLabel(area);
  if (info.ask === "person") return [dish ? `${info.label}: ${dish}` : info.label, where ? `in ${where}` : ""].filter(Boolean).join(" ");
  return `${info.label}${sep}${where}`;
}

/** The answers shown on a report for the manager: "Dish: Lattes", "Allergen: Milk, Soy", "Ate: Yesterday". */
export function incidentAnswerLines(i: { dish?: string; allergens?: string[]; ateWhen?: string; injury?: InjuryAnswers }): string[] {
  const out: string[] = [];
  if (i.dish) out.push(`Dish: ${i.dish}`);
  if (i.allergens?.length) out.push(`${i.allergens.length > 1 ? "Allergens" : "Allergen"}: ${i.allergens.map(allergenLabel).join(", ")}`);
  if (i.ateWhen) out.push(`Ate: ${i.ateWhen}`);
  out.push(...injuryLines(i.injury));
  return out;
}

/** Sick reports never record where (docs/PEOPLE_AND_NEGATION.md T4). */
export const NOT_SHARED_AREA = "Not shared";

/**
 * Where a report happened, by the type's ask. Shared by the API and both demos so they accept the same reports.
 * - private (sick): always "Not shared", whatever was sent.
 * - place: a room from `areas`, or "Somewhere else: <text>", so a missing room never blocks a report. A location with
 *   no rooms takes any place. Anything else is an error.
 * - person: optional; "" when not given; stored as given when it isn't one of the rooms ("Table 4").
 */
export function reportArea(type: IncidentType, area: string | undefined, areas: readonly string[]): { area: string } | { error: string } {
  const ask = incidentInfo(type).ask;
  const a = (area ?? "").trim();
  if (ask === "private") return { area: NOT_SHARED_AREA };
  if (ask === "person") return { area: a };
  if (!a) return { error: "Pick where it is." };
  if (areas.length && !areas.includes(a) && !isSomewhereElse(a)) return { error: `Pick an area from this location (${areas.join(", ")}), or "${SOMEWHERE_ELSE}" and type where.` };
  return { area: a };
}

/**
 * A person report's answers, kept only for the questions its type asks (a pest report's "dish" is dropped; a sick
 * report never stores any). Allergens must be MAJOR_ALLERGENS ids or "not sure"; "not sure" is dropped next to a named one.
 */
export function reportAnswers(type: IncidentType, b: { dish?: string; allergens?: readonly string[]; ateWhen?: string; injury?: unknown }): { answers: { dish?: string; allergens?: string[]; ateWhen?: string; injury?: InjuryAnswers } } | { error: string } {
  const q = incidentInfo(type).questions ?? [];
  const answers: { dish?: string; allergens?: string[]; ateWhen?: string; injury?: InjuryAnswers } = {};
  const dish = b.dish?.trim(), ateWhen = b.ateWhen?.trim();
  if (q.includes("dish") && dish) answers.dish = dish;
  if (q.includes("ateWhen") && ateWhen) answers.ateWhen = ateWhen;
  if (q.includes("allergens") && b.allergens?.length) {
    const bad = b.allergens.find((x) => !ALLERGEN_IDS.includes(x));
    if (bad !== undefined) return { error: `Unknown allergen "${bad}". Pick from: ${ALLERGEN_IDS.join(", ")}.` };
    const picked = [...new Set(b.allergens)];
    answers.allergens = picked.length > 1 ? picked.filter((x) => x !== ALLERGEN_NOT_SURE) : picked;
  }
  // A cut or burn: a doctor needed, blood near food, covered with a bandage and glove (injury.ts). Kept only for a type that asks.
  if (q.includes("hospital") || q.includes("food") || q.includes("covered")) {
    const got = parseInjury(b.injury);
    if ("error" in got) return { error: got.error };
    if (got.injury) answers.injury = got.injury;
  }
  return { answers };
}

/** A screen of a person report's questions (one question per screen; the customer's meal and its time go together). */
export interface QuestionScreen { title: string; questions: IncidentQuestion[] }

/** allergen_reaction: "Which dish?" then "Which allergen?"; customer_illness_complaint: "What did they eat, and when?". */
export function questionScreens(t: IncidentType): QuestionScreen[] {
  const q = incidentInfo(t).questions ?? [];
  const out: QuestionScreen[] = [];
  if (q.includes("dish") && q.includes("ateWhen")) out.push({ title: "What did they eat, and when?", questions: ["dish", "ateWhen"] });
  else if (q.includes("dish")) out.push({ title: "Which dish?", questions: ["dish"] });
  else if (q.includes("ateWhen")) out.push({ title: "When did they eat?", questions: ["ateWhen"] });
  if (q.includes("allergens")) out.push({ title: "Which allergen?", questions: ["allergens"] });
  // A cut or burn: one question per screen, the person's own need first (injury.ts INJURY_QUESTIONS).
  for (const k of ["hospital", "food", "covered"] as const) if (q.includes(k)) out.push({ title: INJURY_QUESTIONS[k].title, questions: [k] });
  return out;
}

/** The "Something else" dish chip, which opens a text field. */
export const SOMETHING_ELSE_DISH = "Something else";

/** Dish chips: the location's menu items (playbooks.ts MENU_ITEMS), by name, in menu order. */
export function dishChoices(menuItemIds: readonly string[]): string[] {
  return MENU_ITEMS.filter((m) => menuItemIds.includes(m.id)).map((m) => m.name);
}

/* ─────────────── Still there / Gone: once, not a tap counter ─────────────── */

/** "Still there" counts once per person per report per hour. */
export const STILL_THERE_EVERY_MS = 3600_000;

/**
 * True when this Still there / Gone would add nothing, so the API (and both demos) return the report as it is, with no
 * audit row and no notice: the report is resolved; this person already said Gone; or this person said Still there in
 * the last hour. Owner: "Why can you press 'It's gone' multiple times on a pest report?"
 */
export function repeatConfirmation(i: { status: string; confirmations: { by: string; at: string; stillThere: boolean }[] }, by: string, stillThere: boolean, now = Date.now()): boolean {
  if (i.status === "resolved") return true;
  const mine = i.confirmations.filter((c) => c.by === by);
  if (!stillThere) return mine.some((c) => !c.stillThere);
  return mine.some((c) => c.stillThere && now - Date.parse(c.at) < STILL_THERE_EVERY_MS);
}

/** This person's settled answer on a report, for the screen: "Marked gone by Sam at 2:14 PM" (and no more buttons). */
export function myConfirmation(i: { confirmations: { by: string; at: string; stillThere: boolean }[] }, by: string, now = Date.now()): { stillThere: boolean; at: string } | undefined {
  const mine = i.confirmations.filter((c) => c.by === by);
  const gone = mine.find((c) => !c.stillThere);
  if (gone) return { stillThere: false, at: gone.at };
  const recent = mine.filter((c) => c.stillThere && now - Date.parse(c.at) < STILL_THERE_EVERY_MS).at(-1);
  return recent ? { stillThere: true, at: recent.at } : undefined;
}
